import { useTranslation } from "react-i18next";

import type { CustomerShipment } from "@/api/queries";
import type { CustomerDispatch } from "@/api/types";
import { pickupKey } from "@/lib/pickupVocabulary";

/**
 * Customer-facing per-shipment card — an order can ship in parts (or be
 * consolidated with others), so each package shows its own status, piece
 * count, and carrier deep-link. Shared by the signed-in order detail
 * (OrderThanks) and the public /track page so both stay in lockstep.
 */

export function CustomerShipmentsCard({
  shipments,
  locale,
  fulfillment,
  dispatch,
  pickup = false,
}: {
  shipments: CustomerShipment[];
  locale: string;
  fulfillment?: "unshipped" | "partial" | "fulfilled";
  /** KAN-1882 — „Odeslali jsme 8 z 10 ks, zbylé 2 ks dovyrábíme…". */
  dispatch?: CustomerDispatch | null;
  /** KAN-2015 — osobní odběr: „Připraveno k vyzvednutí" / „Vydáno", ne „Odesláno". */
  pickup?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation("order");
  const k = (key: string) => pickupKey(key, pickup, "customer");
  if (shipments.length === 0) return null;
  return (
    <section
      className="mt-4 rounded-lg border border-border bg-surface p-4"
      data-testid="customer-shipments"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
          {t(k("shipments.heading"), { defaultValue: "Zásilky" })}
        </h3>
        {fulfillment && fulfillment !== "unshipped" && (
          <span
            className="rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-ink-2"
            data-testid="customer-fulfillment"
          >
            {t(k(`shipments.fulfillment.${fulfillment}`), {
              defaultValue: fulfillment,
            })}
          </span>
        )}
      </div>
      {fulfillment === "partial" && dispatch && dispatch.ordered_pieces > 0 && (
        <p className="mt-2 text-sm text-ink-2" data-testid="customer-dispatch-summary">
          {t(k("shipments.dispatch.shipped"), {
            shipped: dispatch.shipped_pieces,
            ordered: dispatch.ordered_pieces,
          })}{" "}
          {dispatch.remake_pieces > 0
            ? dispatch.remake_estimated_at
              ? t(k("shipments.dispatch.remakeEta"), {
                  n: dispatch.remake_pieces,
                  date: formatDate(dispatch.remake_estimated_at, locale),
                })
              : t(k("shipments.dispatch.remakeNoEta"), {
                  n: dispatch.remake_pieces,
                })
            : dispatch.ordered_pieces > dispatch.shipped_pieces
              ? t(k("shipments.dispatch.restLater"))
              : null}
        </p>
      )}
      <ul className="mt-3 space-y-2">
        {shipments.map((s, i) => (
          <li
            key={s.note_number ?? i}
            className="flex flex-wrap items-center gap-2 text-sm"
            data-testid="customer-shipment"
          >
            <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-ink-2">
              {t(k(`shipments.status.${s.status}`), { defaultValue: s.status })}
            </span>
            <span className="font-medium text-ink">{s.note_number ?? "—"}</span>
            <span className="text-ink-3">
              {t("shipments.pieces", { defaultValue: "{{n}} ks", n: s.pieces })}
            </span>
            {(s.delivered_at ?? s.shipped_at) && (
              <span className="text-xs text-ink-3">
                {formatDate((s.delivered_at ?? s.shipped_at) as string, locale)}
              </span>
            )}
            {s.tracking_code && (
              <span className="ml-auto">
                {s.tracking_url ? (
                  <a
                    href={s.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ink underline hover:text-ink-2"
                    data-testid="customer-shipment-link"
                  >
                    {s.tracking_code}
                  </a>
                ) : (
                  <span className="font-mono text-ink-2">{s.tracking_code}</span>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
}
